/**
 * Nysta — collapsible desktop filter column.
 *
 * The FILTRE label in the collection toolbar is Horizon's own
 * `<h4 class="facets--filters-title">` (blocks/filters.liquid:107), inside a
 * Dwell block this proof does not own. Rather than fork that block, this file
 * upgrades the heading in place into a real toggle control and keeps it in
 * sync; every geometry change is CSS, driven by one attribute.
 *
 * State lives on <html data-nysta-filters="open|collapsed">, deliberately
 * OUTSIDE the section. The collection section is replaced wholesale by the
 * Section Rendering API on every filter, sort and infinite-scroll page, so an
 * attribute on the shell would be wiped by each of them; on the document
 * element it survives all three with no re-application, and the CSS keeps
 * matching through the morph. Only the heading's own ARIA attributes have to
 * be re-applied afterwards, which is what sync() is for.
 *
 * The attribute is set before this file runs, by a tiny inline script at the
 * top of sections/nysta-collection.liquid, so the column is never painted in
 * one state and then flipped.
 *
 * Open or collapsed is remembered per HISTORY ENTRY, in
 * `history.state.nystaFilters`, and nowhere else. The column is collapsed by
 * default; it comes back open only when the user returns (Back/Forward, or a
 * reload) to the very entry where they left it open. That keeps two listings
 * apart without keying anything by collection -- each visit is its own entry
 * -- and a fresh visit to a listing opened earlier still starts collapsed.
 * (It replaces a single sessionStorage value that was shared by every
 * collection and defaulted to open.)
 *
 * Horizon writes history.state itself, and not by merging: filtering pushes
 * `{ urlParameters }` (assets/facets.js), infinite scroll pushes `''`
 * (assets/paginated-list.js), the cart drawer pushes and later clears its own
 * flag (assets/cart-drawer.js). Each of those would silently drop the record,
 * so pushState/replaceState are wrapped to carry it into the object they are
 * handed. Nothing in the theme reads those values back, but they are kept
 * as they are: a plain object is copied with the key added, `null`/`''` (no
 * data) become `{ nystaFilters }`, anything else is passed through untouched.
 *
 * Below 750px nothing here applies: Horizon hides `.facets--filters-title`
 * outright at that width (blocks/filters.liquid:1357-1360) and the mobile
 * filter drawer is a different control entirely, untouched.
 */
(() => {
  const KEY = 'nystaFilters';
  const OPEN = 'open';
  const COLLAPSED = 'collapsed';
  const RAIL_ID = 'nysta-filter-rail';
  const TITLE = '.nysta-collection-shell__grid .facets--filters-title';
  const root = document.documentElement;

  const current = () => (root.getAttribute('data-nysta-filters') === OPEN ? OPEN : COLLAPSED);

  /* The inline script normally sets this before first paint; if it did not
     run, fall back to the same default rather than to the CSS's open layout. */
  if (!root.hasAttribute('data-nysta-filters')) root.setAttribute('data-nysta-filters', COLLAPSED);

  /**
   * Re-applies the heading's control semantics. Idempotent, and cheap enough
   * to run on every section morph: three lookups and a handful of attribute
   * writes.
   */
  const sync = () => {
    document.querySelectorAll('.nysta-collection-shell__grid').forEach((shell) => {
      const title = shell.querySelector('.facets--filters-title');
      if (!title) return;

      const rail = shell.querySelector('div.facets-block-wrapper--vertical');
      if (rail && !rail.id) rail.id = RAIL_ID;

      title.setAttribute('role', 'button');
      title.setAttribute('tabindex', '0');

      /* Below 1200px the control opens a drawer rather than expanding a
         column, so it points at the drawer and reports the drawer's state. */
      const drawer = document.getElementById('filters-drawer');
      const drawerMode = window.matchMedia(`(max-width: ${DRAWER_MAX}px)`).matches;
      if (drawerMode && drawer) {
        const dlg = drawer.querySelector('dialog');
        title.setAttribute('aria-expanded', String(!!(dlg && dlg.hasAttribute('open'))));
        if (drawer.id) title.setAttribute('aria-controls', drawer.id);
        return;
      }

      title.setAttribute('aria-expanded', String(current() === OPEN));
      if (rail && rail.id) title.setAttribute('aria-controls', rail.id);
    });
  };

  /**
   * history.state with the column state merged in, or `undefined` when the
   * existing value is not something a key can be added to without changing
   * what it is (a non-empty string, an array, a class instance).
   */
  const withState = (state, value, overwrite) => {
    if (state === null || state === undefined || state === '') return { [KEY]: value };
    if (typeof state !== 'object' || Array.isArray(state)) return undefined;
    const proto = Object.getPrototypeOf(state);
    if (proto !== Object.prototype && proto !== null) return undefined;
    if (!overwrite && KEY in state) return state;
    return { ...state, [KEY]: value };
  };

  const nativePush = history.pushState;
  const nativeReplace = history.replaceState;

  /** Records the column's current state on the current history entry. */
  const record = () => {
    try {
      if (history.state && history.state[KEY] === current()) return;
      const next = withState(history.state, current(), true);
      if (next !== undefined) nativeReplace.call(history, next, '');
    } catch (error) {
      // History unavailable (sandboxed frame); the column still works, it
      // just is not restored on Back.
    }
  };

  /* Installed once per document, even if this file is evaluated twice. */
  if (!history.pushState.nystaFilters) {
    const wrap = (native) => {
      const wrapped = function (state, ...rest) {
        const next = withState(state, current(), false);
        return native.call(this, next === undefined ? state : next, ...rest);
      };
      wrapped.nystaFilters = true;
      return wrapped;
    };
    history.pushState = wrap(nativePush);
    history.replaceState = wrap(nativeReplace);
  }

  const set = (next) => {
    root.setAttribute('data-nysta-filters', next);
    record();
    sync();
  };

  /* Below 1200px there is no filter column to collapse -- the shell is not a
     grid there and the rail is hidden (sections/nysta-collection.liquid, the
     750-1199 block) -- so FILTRE opens Horizon's own drawer instead. Same
     control, same label, the form it opens is the one Horizon already builds
     for the phone breakpoint. Above 1200px it keeps collapsing the column. */
  const DRAWER_MAX = 1199;

  const openDrawer = () => {
    const drawer = document.getElementById('filters-drawer');
    if (drawer && typeof drawer.showDialog === 'function') {
      drawer.showDialog();
      return true;
    }
    return false;
  };

  const toggle = () => {
    if (window.matchMedia(`(max-width: ${DRAWER_MAX}px)`).matches) {
      if (openDrawer()) return;
    }
    set(current() === OPEN ? COLLAPSED : OPEN);
  };

  /**
   * Delegated on the document, so it survives every section morph without
   * rebinding — the heading element itself is replaced repeatedly.
   */
  const hit = (event) => (event.target instanceof Element ? event.target.closest(TITLE) : null);

  document.addEventListener('click', (event) => {
    if (!hit(event)) return;
    toggle();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    if (!hit(event)) return;
    // Space scrolls the page by default, and Enter on a role=button does not
    // fire a click of its own the way a real <button> would.
    event.preventDefault();
    toggle();
  });

  /**
   * Re-sync after the section is re-rendered. `filter:update` (assets/events.js,
   * ThemeEvents.FilterUpdate) covers filtering and sorting, but it can fire
   * before the replacement DOM lands, so the observer is the one that actually
   * guarantees it. Scoped to the results list rather than the document.
   *
   * No debounce, and deliberately no requestAnimationFrame: a MutationObserver
   * callback already batches everything that happened since the last microtask
   * checkpoint, so a whole infinite-scroll append arrives as one call, and
   * sync() is three lookups. rAF was the first version and was wrong -- it is
   * paused in a background tab, so a section replaced while the tab was hidden
   * kept the old heading with no control semantics until the tab came forward.
   */
  document.addEventListener('filter:update', () => {
    sync();
    // ...and again once the replacement DOM has landed, if it had not yet.
    setTimeout(sync, 0);
  });

  const observe = () => {
    document.querySelectorAll('results-list').forEach((list) => {
      new MutationObserver(sync).observe(list, { childList: true, subtree: true });
    });
    sync();
  };

  /* Below 1200px the control reports the drawer's state, so it has to be
     re-read when the drawer opens or closes -- including by Escape, the
     backdrop or the drawer's own close button, none of which pass through
     toggle(). Horizon's dialog events do not bubble, hence capture. */
  const onDrawer = (event) => {
    if (!(event.target instanceof Element) || event.target.id !== 'filters-drawer') return;
    sync();
    setTimeout(sync, 0);
  };
  document.addEventListener('dialog:open', onDrawer, true);
  document.addEventListener('dialog:close', onDrawer, true);

  /* Back/Forward between entries of this same document (the ones filtering
     and infinite scroll push) does not re-render the listing, so the column
     stays as it is on screen; the entry landed on is brought in line with it,
     so a later cross-document Back returns to what was actually shown. */
  window.addEventListener('popstate', record);
  /* A fragment link (the skip-to-results link) makes a new entry with an
     empty state; record the column on it too. */
  window.addEventListener('hashchange', record);

  /* Restored from the bfcache: the DOM, and so the column, is exactly as it
     was left. Only the control's ARIA is re-derived, in case the viewport
     crossed 1200px while the page was away. */
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) sync();
  });

  /* Crossing 1200px changes what the control does, so it has to change what
     it says about itself at the same moment. */
  window.matchMedia(`(max-width: ${DRAWER_MAX}px)`).addEventListener('change', sync);

  record();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }
})();
